import React, { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import liff from "@line/liff";
import * as api from "../../api/MyHome";
import { DisclaimerContext } from "../../contexts/DisclaimerContext.js";
import { DisclaimerDialog } from "../../ui-component/DisclaimerDialog.js";
import { checkProductType } from "../../utils/checkProductType";

// TODO(allowlist): production deployment should restrict `forward` to known REM
// hosts. Replace this stub with an explicit allowlist (e.g. *.iconrem.com).
const isAllowedForwardUrl = (_url) => true;

const readLineSession = () => {
  try {
    const raw = localStorage.getItem("profile_lineOA");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const DisclaimerGate = () => {
  const [params] = useSearchParams();
  const id = params.get("id");
  const forwardRaw = params.get("forward");
  const disclaimerlogContext = useContext(DisclaimerContext);

  const [disclaimerData, setDisclaimerData] = useState(null);
  const [description, setDescription] = useState("");
  const [isFunction, setIsFunction] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [checkDisclaimerLog, setCheckDisclaimerLog] = useState(false);
  const [showTextReminder, setShowTextReminder] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const hasInitialized = useRef(false);

  const decodedForward = (() => {
    if (!forwardRaw) return "";
    try {
      return decodeURIComponent(forwardRaw);
    } catch {
      return forwardRaw;
    }
  })();

  useEffect(() => {
    if (hasInitialized.current) return;

    if (!id || !decodedForward) {
      setErrorMsg("ลิงก์ไม่ถูกต้อง");
      return;
    }

    if (!isAllowedForwardUrl(decodedForward)) {
      setErrorMsg("ปลายทางไม่ได้รับอนุญาต");
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 50; // ~10s at 200ms

    const waitForSessionThenLoad = async () => {
      while (!cancelled && attempts < maxAttempts) {
        const session = readLineSession();
        if (session?.profile?.userId && session?.cid) {
          await loadDisclaimer(session);
          return;
        }
        attempts += 1;
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!cancelled) {
        setErrorMsg("ไม่สามารถยืนยันตัวตนผ่าน LINE ได้ กรุณาลองใหม่อีกครั้ง");
      }
    };

    const loadDisclaimer = async (session) => {
      hasInitialized.current = true;
      const companyId =
        Number(session.cid) ||
        Number(session.cid_pm) ||
        Number(session.cid_rental);

      try {
        const result = await api.getDisclaimerById({
          id: Number(id),
          company_id: companyId,
        });

        // Distinguish transport failure (404/500/network) from a legitimate
        // "no disclaimer configured" response (200 OK with data: null).
        // axios success path puts the response under result.data; on error,
        // the helper returns the Error object whose .status is undefined.
        if (result?.status !== 200) {
          setErrorMsg("ไม่สามารถโหลดเงื่อนไขได้ กรุณาลองใหม่อีกครั้ง");
          return;
        }

        const data = result?.data?.data;

        if (!data) {
          // API explicitly says no disclaimer configured — bypass gate.
          window.location.href = decodedForward;
          return;
        }

        const acceptResult = await api.getIsAcceptDisclaimer({
          company_id: companyId,
          line_id: session.profile.userId,
          master_disclaimer_id: data.id,
          product_type: checkProductType() || "remlite",
        });
        if (acceptResult?.status !== 200) {
          setErrorMsg("ไม่สามารถตรวจสอบสถานะเงื่อนไขได้ กรุณาลองใหม่อีกครั้ง");
          return;
        }
        const isAccepted = acceptResult?.data?.data?.is_accept === true;
        const requiresShow = data.is_updated === true || !isAccepted;

        if (!requiresShow) {
          window.location.href = decodedForward;
          return;
        }

        if (Array.isArray(data.pictures)) {
          data.pictures = data.pictures.map((picture) => ({
            id: picture.id ? picture.id.toString() : `new-${Math.random()}`,
            name: picture.name,
            url: picture.path,
          }));
        }

        setDescription(data.description || "");
        setIsFunction(data.is_function ? true : true);
        setDisclaimerData(data);
        setOpen(true);
      } catch (err) {
        setErrorMsg("เกิดข้อผิดพลาดในการโหลดเงื่อนไข");
      }
    };

    waitForSessionThenLoad();

    return () => {
      cancelled = true;
    };
  }, [id, decodedForward]);

  const handlePrev = () => {
    setCurrentIndex(
      (prev) =>
        (prev - 1 + (disclaimerData?.pictures?.length || 1)) %
        (disclaimerData?.pictures?.length || 1)
    );
  };

  const handleNext = () => {
    setCurrentIndex(
      (prev) => (prev + 1) % (disclaimerData?.pictures?.length || 1)
    );
  };

  const onCancel = () => {
    setOpen(false);
    setShowTextReminder(false);
    setCheckDisclaimerLog(false);
    // Close the LIFF webview entirely — the gate has no useful in-app fallback
    // because users land here from a LINE message, not from in-app navigation.
    try {
      if (liff && typeof liff.closeWindow === "function") {
        liff.closeWindow();
        return;
      }
    } catch {
      // fall through to browser fallbacks below
    }
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.close();
    }
  };

  const onAccept = async () => {
    const session = readLineSession();
    if (!session) {
      setErrorMsg("ไม่พบข้อมูลผู้ใช้ LINE");
      return;
    }
    const companyId =
      Number(session.cid) ||
      Number(session.cid_pm) ||
      Number(session.cid_rental);

    const houseInfoSelected = (() => {
      try {
        return JSON.parse(localStorage.getItem("houseInfoSelected"));
      } catch {
        return null;
      }
    })();

    const username =
      houseInfoSelected?.customer_name ||
      houseInfoSelected?.customer_name_en ||
      session.profile?.displayName ||
      "";

    const req = {
      master_disclaimer_id: disclaimerData.id,
      user_id: session.profile.userId,
      accept_at: new Date(),
      username,
      menu: disclaimerData.title,
      company_id: companyId,
    };

    disclaimerlogContext.dispatch({
      type: "SET_DISCLAIMER",
      payload: disclaimerData?.id,
    });

    try {
      const res = await api.insertDisclaimerLog(req);
      if (res?.status === 200) {
        await api.updateDisclaimer({
          id: disclaimerData.id,
          accept_at: new Date(),
          is_updated: false,
          company_id: companyId,
        });
      }
    } catch {
      // Fall through to redirect even if logging hiccups — UX priority.
    }

    window.location.href = decodedForward;
  };

  const onClose = () => {
    setOpen(false);
  };

  if (errorMsg) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          p: 3,
          gap: 2,
        }}
      >
        <Typography variant="body1" color="error">
          {errorMsg}
        </Typography>
      </Box>
    );
  }

  if (!open) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DisclaimerDialog
      open={open}
      disclaimerData={disclaimerData}
      description={description}
      currentIndex={currentIndex}
      onPrev={handlePrev}
      onNext={handleNext}
      isFunction={isFunction}
      checkDisclaimerLog={checkDisclaimerLog}
      setCheckDisclaimerLog={setCheckDisclaimerLog}
      showTextReminder={showTextReminder}
      setShowTextReminder={setShowTextReminder}
      onCancel={onCancel}
      onAccept={onAccept}
      onClose={onClose}
    />
  );
};

export default DisclaimerGate;
